import WorkIcon from "@mui/icons-material/Work";
import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ReactNode, useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import {
  DataCard,
  DataCardSection,
  DataFieldRow,
} from "../../../../../../../components/app/provider/patient/chart";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import SuccessModal, {
  useSuccess,
} from "../../../../../../../components/SuccessModal";
import WithInfo from "../../../../../../../components/WithInfo";
import useSelectedPatient, {
  PatientProfile,
} from "../../../../../../../hooks/local-storage/use-selected-patient";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import {
  useCreatePatientCredential,
  useCreatePatientSendCredentialOnEmail,
  useEnableDisablePatientPortal,
  useGetPatientCred,
} from "../../../../../../../queries/patient-chart/portal-access/use-create-patient-cred";
import { useGetPatient } from "../../../../../../../queries/use-get-patient-uuid";
import {
  formatDate,
  formatDateTime,
} from "../../../../../../../utils/date-time-utility";
import { getXtenantId } from "../../../../../../../utils/utils";
import CreatePatientAccessCredential from "./CreatePatientAccessCredential";

const PatientPortalAccess = () => {
  const viewCredentialDisclosure = useDisclosure();
  const { patientId } = useParams();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const { registerChildAction, setChildEndContent } = useOutletContext<{
    registerChildAction?: (fn: (() => void) | null) => void;
    setChildEndContent?: (content: ReactNode) => void;
  }>();

  const [
    isSuccessModalOpened,
    { open: openSuccessModal, close: closeSuccessModal },
  ] = useSuccess();

  const {
    data: patientInfo,
    refetch: refetchPatientInfo,
    dataUpdatedAt,
  } = useGetPatient(patientId);
  const [curPatientData, setCurPatientData] = useSelectedPatient();

  const { mutateAsync: generateCredential } = useCreatePatientCredential();
  const { mutateAsync: getCredential } = useGetPatientCred();
  const { mutateAsync: enableDisablePortal } = useEnableDisablePatientPortal();
  const { mutateAsync: sendPatientCredOnEmail } =
    useCreatePatientSendCredentialOnEmail();

  useEffect(() => {
    // See PatientChartNewLayout: PatientProfile over-declares every field
    // as required compared with the API model.
    if (patientInfo) setCurPatientData(patientInfo as PatientProfile);
  }, [curPatientData, dataUpdatedAt]);

  const hasPortalAccess = !!curPatientData?.patientPortalAccess;
  const hasCredentialGenerated = !!curPatientData?.credentialGenerated;
  const [successMessage, setSuccessMessage] = useState<string>("");

  const handleEnableOrDisableAccess = () => {
    if (hasPortalAccess) {
      enableDisablePortal({
        patientId: patientId ?? "",
        access: false,
        xTENANTID: getXtenantId(),
      })
        .then((res) => {
          setSuccessMessage((res?.data?.message as any) ?? "");
          queryClient.invalidateQueries({ queryKey: ["patient", patientId] });
          openSuccessModal();
        })
        .catch((err: any) =>
          showSnackbar({ type: "error", message: err?.message })
        );
    } else {
      sendPatientCredOnEmail({
        patientId: patientId ?? "",
        xTENANTID: getXtenantId(),
      })
        .then(() => {
          setSuccessMessage(
            "Patient Will Receive Access Information via Email"
          );
          queryClient.invalidateQueries({ queryKey: ["patient", patientId] });
          openSuccessModal();
        })
        .catch((err: any) =>
          showSnackbar({ type: "error", message: err?.message })
        );
    }
  };

  const shouldDisableAccess = () => {
    if (curPatientData?.email && !hasCredentialGenerated) return true;
    if (!curPatientData?.email && hasCredentialGenerated !== null) return false;
    if (hasCredentialGenerated === null) return true;
    if (!curPatientData?.email) return true;
    return false;
  };

  const handleViewCredential = () => {
    getCredential({
      patientId: patientId ?? "",
      xTENANTID: getXtenantId(),
    })
      .then((res: any) => {
        const data = res?.data?.data;
        viewCredentialDisclosure.open({
          userId: data?.username ?? "",
          password: data?.password ?? "",
        });
      })
      .catch((err: any) =>
        showSnackbar({ type: "error", message: err?.message })
      );
  };

  const handleCreateCredential = () => {
    generateCredential({
      patientId: patientId ?? "",
      xTENANTID: getXtenantId(),
    })
      .then((res: any) => {
        const data = res?.data?.data;
        queryClient.invalidateQueries({ queryKey: ["patient", patientId] });
        viewCredentialDisclosure.open({
          userId: data?.username ?? "",
          password: data?.password ?? "",
        });
      })
      .catch((err: any) =>
        showSnackbar({ type: "error", message: err?.message })
      );
  };

  const handleEdit = () => {
    if (hasCredentialGenerated) {
      handleViewCredential();
    } else {
      handleCreateCredential();
    }
  };

  // Register credential creation handler for the tertiary navbar button
  useEffect(() => {
    registerChildAction?.(() => handleCreateCredential());
    return () => registerChildAction?.(null);
  });

  // Register Enable/Disable button in the tertiary navbar
  useEffect(() => {
    setChildEndContent?.(
      <WithInfo
        title="Create Credential to enable patient portal access"
        hideInfoIcon={hasCredentialGenerated}
      >
        <StyledButtonV2
          label={`${hasPortalAccess ? "Disable" : "Enable"} Patient Access`}
          onClick={handleEnableOrDisableAccess}
          variant="outlined"
          startIcon={<WorkIcon />}
          size="small"
          disabled={shouldDisableAccess()}
        />
      </WithInfo>
    );
    return () => setChildEndContent?.(null);
  });

  const sections: DataCardSection[] = [
    {
      rows: [
        {
          cells: [
            <DataFieldRow
              label="Date of Birth"
              stretchValue
              value={
                curPatientData?.birthDate
                  ? formatDate(curPatientData.birthDate)
                  : "-"
              }
            />,
            <DataFieldRow
              label="Contact No."
              stretchValue
              value={curPatientData?.mobileNumber ?? "-"}
            />,
          ],
        },
        {
          cells: [
            <DataFieldRow
              label="Last Sign In"
              stretchValue
              value={
                curPatientData?.lastLogin
                  ? formatDateTime(curPatientData.lastLogin)
                  : "-"
              }
            />,
            <DataFieldRow
              label="E-mail"
              stretchValue
              value={curPatientData?.email ?? "-"}
            />,
          ],
        },
      ],
    },
  ];

  return (
    <Box p={1.5}>
      <CreatePatientAccessCredential
        disclosureHost={viewCredentialDisclosure}
        onSuccess={refetchPatientInfo}
        viewMode
      />
      <SuccessModal
        label={successMessage}
        opened={isSuccessModalOpened}
        onClose={closeSuccessModal}
      />
      <DataCard
        title="Patient Portal Access"
        onEdit={handleEdit}
        sections={sections}
        sx={{ maxWidth: "800px" }}
      />
    </Box>
  );
};

export default PatientPortalAccess;
